<template>
  <div ref="anchorEl">
    <!-- Admin Menu Button -->
    <button
      @click="toggleSlideout"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :class="[
        'relative grid grid-cols-[70px_minmax(0,1fr)_16px] items-center py-2 text-sm font-medium transition-colors duration-200 rounded-lg overflow-hidden',
        isActive ? 'text-white' : 'text-slate-300 hover:text-white hover:bg-slate-700/50',
      ]"
    >
      <img
        :src="props.iconSrc"
        alt="User Admin icon"
        class="shrink-0 transition-all duration-200 justify-self-center"
        :class="[
          'w-[30px] h-[30px]',
          isActive ? 'sidebar-icon-active' : '',
        ]"
      />
      <span
        class="whitespace-nowrap transition-all duration-200 pr-3"
        :class="props.isExpanded ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'"
      >
        User Admin
      </span>
      <svg
        v-if="props.isExpanded"
        :class="['w-4 h-4 justify-self-end transition-transform duration-200', isOpen ? 'rotate-180' : '']"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <!-- Chevron Right; rotates 180° to become Chevron Left when open -->
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        ></path>
      </svg>
    </button>

    <teleport to="body">
      <!-- Backdrop -->
      <transition name="fade-fast">
        <div v-if="isOpen" class="fixed inset-0 bg-black/50 z-[10000]" @click="closeSlideout"></div>
      </transition>

      <!-- Slide-out Panel (anchored beside sidebar + margin-left) -->
      <Motion
        v-if="isOpen"
        class="fixed w-80 bg-slate-900/95 backdrop-blur border-l border-slate-700/60 shadow-2xl z-[10001] will-change-transform"
        :style="{ left: 'calc(12.625rem + 2rem)', top: panelTop + 'px', height: panelHeight + 'px' }"
        :initial="{ x: -320, opacity: 0 }"
        :animate="{ x: 0, opacity: 1 }"
        :exit="{ x: -320, opacity: 0 }"
        :transition="{ type: 'spring', stiffness: 560, damping: 28, mass: 0.9 }"
        @click.stop
      >
        <div class="flex flex-col h-full">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-slate-700">
            <div>
              <h2 class="text-lg font-semibold text-white">User Admin</h2>
              <p class="text-sm text-slate-400">System administration</p>
            </div>
            <button
              @click="closeSlideout"
              class="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>

          <!-- Menu Items -->
          <div class="flex-1 overflow-y-auto p-6">
            <nav class="space-y-2" @click="closeSlideout">
              <!-- Users Management -->
              <router-link
                to="/users"
                class="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors group"
              >
                <div
                  class="p-2 bg-blue-600/20 rounded-lg group-hover:bg-blue-600/30 transition-colors"
                >
                  <svg
                    class="w-5 h-5 text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <div class="font-medium">User Management</div>
                  <div class="text-sm text-slate-500">Manage users and permissions</div>
                </div>
              </router-link>

              <!-- Role Management -->
              <router-link
                to="/roles"
                class="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors group"
              >
                <div
                  class="p-2 bg-green-600/20 rounded-lg group-hover:bg-green-600/30 transition-colors"
                >
                  <svg
                    class="w-5 h-5 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <div class="font-medium">Role Management</div>
                  <div class="text-sm text-slate-500">Configure roles and permissions</div>
                </div>
              </router-link>

              <!-- Security Management -->
              <router-link
                to="/security"
                class="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors group"
              >
                <div
                  class="p-2 bg-red-600/20 rounded-lg group-hover:bg-red-600/30 transition-colors"
                >
                  <svg
                    class="w-5 h-5 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    ></path>
                  </svg>
                </div>
                <div>
                  <div class="font-medium">Security Policies</div>
                  <div class="text-sm text-slate-500">Manage RLS policies and security</div>
                </div>
              </router-link>

              <!-- Divider -->
              <div class="border-t border-slate-700 my-4"></div>

              <!-- System Status -->
              <div class="px-4 py-3 bg-slate-700/30 rounded-lg">
                <div class="flex items-center gap-2 mb-2">
                  <div class="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span class="text-sm font-medium text-white">System Status</span>
                </div>
                <div class="text-xs text-slate-400">All systems operational</div>
              </div>
            </nav>
          </div>

          <!-- Footer -->
          <div class="p-6 border-t border-slate-700">
            <div class="text-xs text-slate-500 text-center">Admin Panel v1.0</div>
          </div>
        </div>
      </Motion>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, watch, onMounted, nextTick, computed } from 'vue'
import type { Ref } from 'vue'
import { useRoute } from 'vue-router'
import { Motion } from 'motion-v'

const props = withDefaults(
  defineProps<{
    isExpanded?: boolean
    iconSrc?: string
  }>(),
  {
    isExpanded: true,
    iconSrc: '/svg-icons/key2.svg',
  },
)

const isOpen = ref(false)

const emit = defineEmits<{
  (e: 'adminSlideoutToggle', open: boolean): void
}>()

const injectedOpen = inject<Ref<boolean>>('adminSlideoutOpen')
if (injectedOpen) {
  watch(
    injectedOpen,
    (value) => {
      isOpen.value = value
    },
    { immediate: true },
  )
}

// Match sidebar panel height
const anchorEl = ref<HTMLElement | null>(null)
const panelTop = ref(0)
const panelHeight = ref(window.innerHeight)

function measurePanelBounds() {
  const el = anchorEl.value
  if (!el) return
  // Find the nearest .panel-neo container (sidebar glass panel)
  const panel = el.closest('.panel-neo') as HTMLElement | null
  if (panel) {
    const rect = panel.getBoundingClientRect()
    panelTop.value = rect.top
    panelHeight.value = rect.height
  } else {
    // Fallback: occupy full viewport minus header guess
    panelTop.value = 0
    panelHeight.value = window.innerHeight
  }
}

onMounted(() => {
  nextTick(measurePanelBounds)
  window.addEventListener('resize', measurePanelBounds)
})

const toggleSlideout = () => {
  isOpen.value = !isOpen.value
  emit('adminSlideoutToggle', isOpen.value)
  if (isOpen.value) nextTick(measurePanelBounds)
}

const closeSlideout = () => {
  isOpen.value = false
  emit('adminSlideoutToggle', false)
}

// Close on escape key
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeSlideout()
  }
}

// Add event listener
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleEscape)
}

// Close on route change
const route = useRoute()
const isActive = computed(() => {
  const adminRoutes = new Set(['/users', '/roles', '/security'])
  if (isOpen.value) return true
  if (adminRoutes.has(route.path)) return true
  return route.path.startsWith('/users/') || route.path.startsWith('/roles/') || route.path.startsWith('/security/')
})
watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) closeSlideout()
  },
)
</script>

<style scoped>
.sidebar-icon-active {
  filter: brightness(0) saturate(100%) invert(92%) sepia(96%) saturate(6137%) hue-rotate(2deg) brightness(106%) contrast(106%);
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 120ms ease;
}
.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

.slide-in-right-enter-active,
.slide-in-right-leave-active {
  transition:
    transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1.2),
    opacity 180ms ease;
}
.slide-in-right-enter-from,
.slide-in-right-leave-to {
  transform: translateX(-12px);
  opacity: 0;
}
</style>
